import {
  CUSTOM_REPORT_CONFIGURATION_SETTINGS,
  parseCustomReportConfiguration,
  useEquipmentEmissionsRowOptions,
  useEquipmentRecordsRowOptions,
  useFacilityRecordsRowOptions,
  useFlowRecordsRowOptions,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import {
  CustomReportVariants,
  CustomReportVariantType,
  getConfigFromSavedFilter,
  useSavedCustomReport,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout.helpers";
import { useListCalculatorResults } from "#hooks/adapters/useCalculatorResults";
import { useListRecords } from "#hooks/adapters/useRecords";
import { useListReportingGroups } from "#hooks/adapters/useReportingGroups";
import { useLocation, useParams } from "#routers/hooks";
import { CustomReportsDetailRoutePath } from "#routes/reports/custom-reports/[reportVariant]/[reportId]/detail";
import { ReportingGroupFilterSwitcher } from "#src/batteries-included-components";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { hydrateDateRange } from "#utils/date";
import {
  Button,
  DateSelectorInput,
  DropdownInput,
  Form,
  Panel,
  useHookForm,
  useHookWatch,
} from "@validereinc/common-components";
import { AssetType } from "@validereinc/domain";
import { Assets } from "@validereinc/domain-controllers";
import {
  convertDateRangeToISO,
  DEFAULT_DATE_RANGES,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { isEqual, uniq } from "es-toolkit/compat";
import React, { useEffect, useMemo } from "react";
import styles from "./CustomReportConfigurationPanel.module.scss";

const cx = classNames.bind(styles);

const {
  view: { AssetTypeOptions },
} = Assets;

export const CustomReportConfigurationPanel = ({
  configStorageKey,
  onClear,
  variant,
}: {
  configStorageKey: string;
  onClear?: () => void;
  variant: CustomReportVariantType;
}) => {
  const { reportId } =
    useParams<
      Exclude<typeof CustomReportsDetailRoutePath.pathParams, undefined>
    >();
  const { state } = useLocation();
  const { localize } = useLocalization();
  const { measurementTypes, isLoading: isMeasurementTypesLoading } =
    useMeasurementTypes();
  const {
    data: savedFilter,
    query: { isLoading: isLoadingCustomReport },
  } = useSavedCustomReport(variant, reportId);
  const [configuration, setConfiguration] = useStickyState(
    {},
    configStorageKey
  );

  const { assetType, dateRange } =
    parseCustomReportConfiguration(configuration);

  const localizedAssetTypeOptions = useMemo(() => {
    return AssetTypeOptions.filter(
      ({ value }) =>
        value !== AssetType.ASSET_GROUP && value !== AssetType.DEVICE
    ).map(({ label, value }) => ({
      label: localize(label),
      value,
    }));
  }, [AssetTypeOptions]);
  const equipmentEmissionsRowOptions = useEquipmentEmissionsRowOptions({
    enabled:
      variant === CustomReportVariants.EMISSIONS ||
      assetType === AssetType.EQUIPMENT,
  });
  const facilityRecordsRowOptions = useFacilityRecordsRowOptions({
    enabled:
      variant === CustomReportVariants.RECORDS &&
      assetType === AssetType.FACILITY,
  });
  const equipmentRecordsRowOptions = useEquipmentRecordsRowOptions({
    enabled:
      variant === CustomReportVariants.RECORDS &&
      assetType === AssetType.EQUIPMENT,
  });
  const flowRecordsRowOptions = useFlowRecordsRowOptions({
    enabled: variant === CustomReportVariants.RECORDS,
  });

  const reportingGroupQuery = useListReportingGroups();

  const resultsQuery = useListCalculatorResults(
    {},
    {
      enabled: variant === CustomReportVariants.EMISSIONS,
    }
  );

  const recordsQuery = useListRecords(
    {
      filters: {
        // @ts-expect-error is this fine?
        year_month: convertDateRangeToISO(
          // @ts-expect-error this is fine
          dateRange ?? DEFAULT_DATE_RANGES.lastTwelveMonths
        ),
        // @ts-expect-error is this fine?
        asset_type: { $eq: assetType },
      },
    },
    { enabled: variant === CustomReportVariants.RECORDS && !!assetType }
  );

  const resultMeasurementTypes =
    uniq(
      resultsQuery.data?.data?.flatMap(({ measurement }) =>
        Object.keys(measurement)
      )
    ) ?? [];

  const recordMeasurementTypes =
    uniq(
      recordsQuery.data?.data?.flatMap(({ values }) =>
        values.map(({ measurement_type }) => measurement_type)
      )
    ) ?? [];

  let rowOptions: Array<{ label: string; value: string }> = [];
  let outputOptions: Array<{ label: string; value: string }> = [];
  let defaultOutputs: string[] = [];
  let isLoading =
    isMeasurementTypesLoading ||
    reportingGroupQuery.isLoading ||
    isLoadingCustomReport;

  switch (variant) {
    case CustomReportVariants.EMISSIONS:
      isLoading = isLoading || resultsQuery.isLoading;
      rowOptions = equipmentEmissionsRowOptions;
      defaultOutputs = ["mass_ch4", "mass_co2", "mass_co2eq", "mass_n2o"];
      outputOptions = measurementTypes
        .filter(
          ({ quantity, id }) =>
            quantity !== "volume" &&
            (resultMeasurementTypes.includes(id) || defaultOutputs.includes(id))
        )
        .map(({ id, name }) => ({ label: name, value: id }));
      break;
    case CustomReportVariants.RECORDS:
      isLoading = isLoading || (assetType && recordsQuery.isLoading);
      rowOptions =
        assetType === AssetType.FACILITY
          ? facilityRecordsRowOptions
          : assetType === AssetType.EQUIPMENT
            ? equipmentRecordsRowOptions
            : assetType === AssetType.FLOW
              ? flowRecordsRowOptions
              : [];
      defaultOutputs = assetType === AssetType.FLOW ? ["volume"] : [];
      outputOptions = measurementTypes
        .filter(({ quantity, id }) =>
          assetType === AssetType.FLOW
            ? quantity === "volume" &&
              (recordMeasurementTypes.includes(id) ||
                defaultOutputs.includes(id))
            : recordMeasurementTypes.includes(id) || defaultOutputs.includes(id)
        )
        .map(({ id, name }) => ({ label: name, value: id }));
      break;
  }

  const defaultValues = {
    [CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.name]: assetType
      ? assetType
      : variant === CustomReportVariants.RECORDS
        ? AssetType.FLOW
        : AssetType.EQUIPMENT,
    [CUSTOM_REPORT_CONFIGURATION_SETTINGS.rows.name]: [],
    [CUSTOM_REPORT_CONFIGURATION_SETTINGS.outputs.name]: defaultOutputs,
    [CUSTOM_REPORT_CONFIGURATION_SETTINGS.reportingScenario.name]: (
      reportingGroupQuery.data as any
    )?.data[0]?.id,
    [CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.name]:
      DEFAULT_DATE_RANGES.lastTwelveMonths,
  };

  const form = useHookForm({
    defaultValues: async () => {
      if (!savedFilter && !state) return defaultValues;

      // parse configuration from custom report's filter
      const { config } = getConfigFromSavedFilter(
        variant,
        savedFilter ?? state
      );

      // sync configuration to sticky state
      setConfiguration(config ?? {});

      return {
        ...config,
        ...(config?.date_range
          ? {
              [CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.name]:
                hydrateDateRange(config.date_range as any),
            }
          : {}),
      };
    },
  });

  const dateRangeSelection = useHookWatch({
    control: form.control,
    name: CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.name,
  });

  const assetTypeSelection = useHookWatch({
    control: form.control,
    name: CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.name,
  });

  const onSubmit = (values: Record<string, any>) => {
    const newValues = values ?? defaultValues;

    setConfiguration(newValues);
    form.reset(newValues);
  };

  // sync asset type selection change
  useEffect(() => {
    if (
      variant !== CustomReportVariants.RECORDS ||
      !assetTypeSelection ||
      assetTypeSelection === assetType
    ) {
      if (
        variant === CustomReportVariants.EMISSIONS &&
        (!assetTypeSelection || !assetType)
      ) {
        setConfiguration((prevValues) => {
          const newConfig = {
            ...prevValues,
            [CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.name]:
              AssetType.EQUIPMENT,
          };

          form.reset(newConfig);

          return newConfig;
        });
      }

      return;
    }

    setConfiguration((prevValues) => {
      const newConfig = {
        ...prevValues,
        [CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.name]:
          assetTypeSelection,
        [CUSTOM_REPORT_CONFIGURATION_SETTINGS.rows.name]: [],
        [CUSTOM_REPORT_CONFIGURATION_SETTINGS.outputs.name]: [],
      };

      form.reset(newConfig);

      return newConfig;
    });
  }, [variant, assetType, assetTypeSelection, form.reset]);

  // sync date range selection change
  useEffect(() => {
    if (isEqual(dateRangeSelection, dateRange)) return;

    setConfiguration((prevValues) => {
      const newConfig = {
        ...prevValues,
        [CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.name]:
          dateRangeSelection,
      };

      form.reset(newConfig);

      return newConfig;
    });
  }, [dateRangeSelection, form.reset]);

  return (
    <Panel
      title="Configuration"
      loaded={!isLoading}
    >
      <Form
        {...form}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        // @ts-expect-error probably null for a reason
        onBlur={null}
      >
        <div className={cx("toolboxContainer")}>
          <ReportingGroupFilterSwitcher
            key={CUSTOM_REPORT_CONFIGURATION_SETTINGS.reportingScenario.name}
            name={CUSTOM_REPORT_CONFIGURATION_SETTINGS.reportingScenario.name}
            style={{ marginBottom: 8 }}
            shouldSelectFirstOptionAsDefault
          />

          <DateSelectorInput
            key={CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.name}
            name={CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.name}
            label={CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.label}
            variant="month"
            isFluid
            isRange
            isRequired
          />

          <DropdownInput
            key={CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.name}
            name={CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.name}
            label={CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.label}
            labelKey="label"
            valueKey="value"
            options={localizedAssetTypeOptions}
            placeholder={
              CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.placeholder
            }
            isFluid
            isSearchable
            isRequired
            isDisabled={variant === CustomReportVariants.EMISSIONS}
          />

          <DropdownInput
            key={CUSTOM_REPORT_CONFIGURATION_SETTINGS.rows.name}
            name={CUSTOM_REPORT_CONFIGURATION_SETTINGS.rows.name}
            label={CUSTOM_REPORT_CONFIGURATION_SETTINGS.rows.label}
            labelKey="label"
            valueKey="value"
            options={rowOptions}
            placeholder={CUSTOM_REPORT_CONFIGURATION_SETTINGS.rows.placeholder}
            isFluid
            isMulti
            isSearchable
            isRequired
            isDisabled={
              variant === CustomReportVariants.RECORDS && !assetTypeSelection
            }
          />
          <DropdownInput
            key={CUSTOM_REPORT_CONFIGURATION_SETTINGS.outputs.name}
            name={CUSTOM_REPORT_CONFIGURATION_SETTINGS.outputs.name}
            label={CUSTOM_REPORT_CONFIGURATION_SETTINGS.outputs.label}
            labelKey="label"
            valueKey="value"
            options={outputOptions}
            placeholder={
              CUSTOM_REPORT_CONFIGURATION_SETTINGS.outputs.placeholder
            }
            isFluid
            isMulti
            isSearchable
            isRequired
            isDisabled={
              variant === CustomReportVariants.RECORDS && !assetTypeSelection
            }
          />
        </div>
        <div className={cx("toolboxActions")}>
          <Button
            type="button"
            variant="outline"
            className={cx("toolboxAction")}
            onClick={() => {
              form.reset(defaultValues);
              setConfiguration(defaultValues);
              onClear?.();
            }}
          >
            Clear
          </Button>
          <Button
            type="submit"
            variant="primary"
            className={cx("toolboxAction")}
            disabled={!form.formState.isDirty || form.formState.isSubmitting}
          >
            Submit
          </Button>
        </div>
      </Form>
    </Panel>
  );
};
